"use client"

import * as React from "react"

import {
  findProductFeatureForNavUrl,
  ProductFeatureBadge,
} from "@ibada/shared-types/product-features"

import {
  ChurchNavBadgeVariant,
  type ChurchNavBadge,
  type ChurchNavSection,
} from "@/features/dashboard/model/church-nav"
import { useProductFeatureMap } from "@/features/product-features/services/product-features-query"

/**
 * "New" is the colour of something good arriving; "Beta" is a caution. Reusing the nav's existing variants
 * rather than introducing a third palette — the badge component is already shared with the admin sidebar.
 */
const BADGE_STYLE: Record<ProductFeatureBadge, ChurchNavBadge> = {
  [ProductFeatureBadge.New]: {
    label: "productFeatures.badges.new",
    variant: ChurchNavBadgeVariant.Success,
  },
  [ProductFeatureBadge.Beta]: {
    label: "productFeatures.badges.beta",
    variant: ChurchNavBadgeVariant.Warning,
  },
}

/**
 * Puts the operator-controlled badge on the nav section that owns each feature.
 *
 * WHY A URL MATCH. The nav is curated in two files and the same feature is reached by different paths from a
 * branch and from the parish — Ministries is `/members/ministries` inside a branch and `/ministries` from
 * the organisation. The registry records the routes a feature owns, so one row lights up both navs without
 * either file naming a feature key.
 *
 * A section that already declares its own badge keeps it. Nothing in the product does today (the hardcoded
 * Reports badge moved into the database), but a hand-written badge is a deliberate statement by whoever
 * wrote it, and silently overwriting it from a table would be the reverse of the control this adds.
 */
export function useProductFeatureBadgeOverlay(sections: ChurchNavSection[]): ChurchNavSection[] {
  const featureMap = useProductFeatureMap()

  return React.useMemo(() => {
    if (featureMap.size === 0) return sections

    let changed = false

    /** A row's badge, or null when it owns no feature, the feature has none, or it declares its own. */
    const badgeFor = (url: string, existing: ChurchNavBadge | undefined): ChurchNavBadge | null => {
      if (existing) return null
      const definition = findProductFeatureForNavUrl(url)
      if (!definition) return null
      const badge = featureMap.get(definition.key)?.badge
      return badge ? BADGE_STYLE[badge] : null
    }

    const next = sections.map((section) => {
      /*
       * Sub-items get badges too.
       *
       * Half the registry lives one level down — Groups and Ministries under Members, Pledges under Finance —
       * so walking only the top level meant an operator could mark Groups "Beta" and see nothing anywhere.
       * The section row and its children are answered by the same lookup.
       */
      let itemChanged = false
      const items = section.items.map((item) => {
        const badge = badgeFor(item.url, item.badge)
        if (!badge) return item
        itemChanged = true
        return { ...item, badge }
      })

      const sectionBadge = badgeFor(section.url, section.badge)
      // `.map` always returns a new array, so the flag — not array identity — decides whether anything moved.
      if (!sectionBadge && !itemChanged) return section

      changed = true
      return {
        ...section,
        ...(itemChanged ? { items } : {}),
        ...(sectionBadge ? { badge: sectionBadge } : {}),
      }
    })

    // Returning the same array when nothing matched keeps the sidebar's memoisation intact.
    return changed ? next : sections
  }, [sections, featureMap])
}
